import { supabase } from './supabase';
import { Like, DailyLikes } from '../types/likes';
import { getClientId } from './clientId';

// Get today's most liked content
export const getTodayTopLikes = async (): Promise<Like[]> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('likes')
      .select('*')
      .gte('last_liked_at', today.toISOString())
      .order('likes_count', { ascending: false });

    if (error) {
      console.error('Error getting top likes:', error);
      return [];
    }
    
    return (data || []).map(like => ({
      id: like.id,
      type: like.type,
      content: like.content,
      timestamp: like.last_liked_at,
      likes: like.likes_count
    }));
  } catch (error) {
    console.error('Error getting top likes:', error);
    return [];
  }
};

// Check if user has liked content today
export const hasLikedToday = async (type: Like['type'], content: string): Promise<boolean> => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const clientId = getClientId();

    const { data, error } = await supabase
      .from('user_likes')
      .select('id')
      .eq('type', type)
      .eq('content', content)
      .eq('client_id', clientId)
      .eq('created_date', today)
      .maybeSingle();

    if (error) {
      console.error('Error checking like status:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking like status:', error);
    return false;
  }
};

// Get likes count for specific content
export const getLikesCount = async (type: Like['type'], content: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('likes')
      .select('likes_count')
      .eq('type', type)
      .eq('content', content)
      .maybeSingle();

    if (error) {
      console.error('Error getting likes count:', error);
      return 0;
    }
    
    return data?.likes_count || 0;
  } catch (error) {
    console.error('Error getting likes count:', error);
    return 0;
  }
};

// Add or update a like
export const updateLike = async (type: Like['type'], content: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    const clientId = getClientId();

    // Check if already liked today
    const hasLiked = await hasLikedToday(type, content);
    if (hasLiked) {
      throw new Error('Already liked today');
    }

    // Call the RPC function to increment like
    const { data: result, error: rpcError } = await supabase.rpc('rpc_increment_like', {
      p_type: type,
      p_content: content,
      p_client_id: clientId,
      p_user_id: userId || null
    });

    if (rpcError) {
      console.error('Error in rpc_increment_like:', rpcError);
      throw rpcError;
    }

    return result;
  } catch (error) {
    console.error('Error updating like:', error);
    throw error;
  }
};

// Get historical likes
export const getHistoricalLikes = async (): Promise<DailyLikes[]> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('likes')
      .select('*')
      .lt('last_liked_at', today.toISOString())
      .order('last_liked_at', { ascending: false });

    if (error) {
      console.error('Error getting historical likes:', error);
      return [];
    }

    // Group by date
    const grouped = (data || []).reduce((acc: { [key: string]: Like[] }, like) => {
      const date = new Date(like.last_liked_at).toISOString().split('T')[0];
      if (!acc[date]) acc[date] = [];
      acc[date].push({
        id: like.id,
        type: like.type,
        content: like.content,
        timestamp: like.last_liked_at,
        likes: like.likes_count
      });
      return acc;
    }, {});

    return Object.entries(grouped).map(([date, likes]) => ({
      date,
      likes
    }));
  } catch (error) {
    console.error('Error getting historical likes:', error);
    return [];
  }
};